import { Box, Chip, List, ListDivider, ListItem, Stack, Tooltip, Typography, useColorScheme, useTheme } from '@mui/joy';
import { VenueIconList } from 'components/venue/VenueIconList';
import type { SubAccountType } from './Account.types';
import VenueLabel from 'components/venue/VenueLabel';
import { Fragment, type ReactNode } from 'react';

const AccountCardSubAccounts = ({ subAccounts }: { subAccounts: SubAccountType[] }): ReactNode => {
  const { mode } = useColorScheme();

  const mapSubAccountsToVenues = (): {
    name: string;
    venue: {
      label: string;
    };
  }[] => {
    return subAccounts.map((subAccount) => ({
      name: subAccount.name,
      venue: {
        label: subAccount.account.venue.label,
      },
    }));
  };

  const mappedSubAccounts = mapSubAccountsToVenues();

  const theme = useTheme();
  return (
    <Stack direction="column" spacing={0.5} paddingRight=".4rem" alignItems="flex-end" justifyContent="flex-end">
      <Tooltip
        placement="bottom-end"
        variant="outlined"
        sx={{
          border: mode === 'dark' ? 'none' : '1px solid neutral.200',
        }}
        title={
          <List
            size="md"
            variant="plain"
            sx={{
              maxWidth: '13rem',
              maxHeight: '20rem',
              overflow: 'auto',
            }}
          >
            <Stack
              direction="row"
              p=".5rem"
              width={'100%'}
              justifyContent="flex-start"
              alignItems="start"
              bgcolor={theme.palette.background.level1}
            >
              <Chip size="sm" variant="soft" color="primary">
                {mappedSubAccounts.length}
              </Chip>
              <Typography level="body-xs" paddingLeft=".5rem">
                Sub Accounts
              </Typography>
            </Stack>
            {mappedSubAccounts.map((subAccount, i) => (
              <Fragment key={`${subAccount.name}-${subAccount.venue.label}`}>
                <ListItem
                  sx={{
                    padding: '0 .4rem',
                    cursor: 'default',
                    '&:not(.Mui-selected, [aria-selected="true"]):hover': {
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  <VenueLabel accountName={subAccount.name} venue={subAccount.venue.label} format="short" />
                  <Typography level="body-xs" marginLeft=".2rem">
                    {subAccount.name}
                  </Typography>
                </ListItem>
                {i < mappedSubAccounts.length - 1 && <ListDivider inset="gutter" />}
              </Fragment>
            ))}
          </List>
        }
      >
        {/* Without Box VenueIconList will not trigger the Tooltip */}
        <Box>
          <VenueIconList maxItems={2} accounts={mappedSubAccounts} />
        </Box>
      </Tooltip>
    </Stack>
  );
};

export default AccountCardSubAccounts;
